<template>
    <div class="flex-1">
        <div ref="player" class="reel-player aspect-16-9 relative" :class="{ '-fullscreen': isFullScreen }" v-if="isPlaying">
            <div class="flex flex-col absolute inset-0">
                <div class="reel-clip-player bg-black relative w-full flex-1" :class="{ '-fullscreen': isFullScreen }">
                    <ClipPlayer :namespace="namespace" 
                        v-for="clip in switchedClips"
                        :key="clip.id"
                        :clipId="clip.id"
                        @click="togglePause()"
                    />
                </div>

                <BigPlayButton v-if="!hasEverPlayed" @click="resume()" />

                <ControlBar :namespace="namespace" class="absolute bottom-0 left-0 right-0" />
            </div>

            <div v-if="showFooter" class="reel-footer" />
        </div>
    </div>
</template>


<script>
    import ClipPlayer from './ClipPlayer'
    import ReelScrubber from './ReelScrubber'
    import ControlBar from './ControlBar'
    import BigPlayButton from './BigPlayButton'
    import { mapGetters, mapState, mapActions } from '@/utils/namespaced-mappings'
    import fullScreenEmitter from '@/utils/fullScreenEmitter'
    import requestFullscreen from '@/utils/requestFullscreen'
    import exitFullscreen from '@/utils/exitFullscreen'
    import isIos from '@/utils/isIos'

    export default {
        props: {
            namespace: { type: String, required: true },
            autoplay: { type: Boolean, default: false },
        },

        components: {
            ClipPlayer,
            ReelScrubber,
            ControlBar,
            BigPlayButton,
        },

        data() {
            return {
                keyPressListener: null,
                fullScreenEmitterListener: null,
                fullScreenChangeListener: null,
                showFooter: isIos(),
            }
        },

        computed: {
            ...mapState([
                'playingClipId',
                'isPaused',
                'isFullScreen',
                'hasEverPlayed',
                'relativeTimeSetting',
            ]),

            ...mapGetters(['isPlaying', 'clipsInPlayOrder']),

            switchedClips() {
                const clips = this.clipsInPlayOrder

                const playingIndex = clips.findIndex(c => c.id == this.playingClipId)

                if (playingIndex !== -1) {
                    return clips.slice(playingIndex, playingIndex + 2)
                }

                return clips.slice(0, 2)
            }
        },

        methods: {
            ...mapActions([
                'togglePause',
                'pause',
                'resume',
                'setFullScreen'
            ]),

            togglePause() {
                this.isPaused ? this.resume() : this.pause()
            },
        },

        mounted() {
            document.addEventListener('keypress', this.keyPressListener = e => {  
                if (e.key == ' ') {
                    this.togglePause()
                    e.preventDefault()
                }
            })

            document.addEventListener('fullscreenchange', this.fullScreenChangeListener = () => {
                if (!document.fullscreen) {
                    this.setFullScreen(false)
                }
            })

            fullScreenEmitter.on(this.fullScreenEmitterListener = () => requestFullscreen(this.$refs.player))

            if (this.autoplay) {
                this.resume()
            }
        },

        beforeDestroy() {
            document.removeEventListener('keypress', this.keyPressListener)
            document.removeEventListener('fullscreenchange', this.fullScreenChangeListener)

            fullScreenEmitter.off(this.fullScreenEmitterListener)
        },

        watch: {
            isFullScreen(isFullScreen) {
                if (!isFullScreen) {
                    exitFullscreen()
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
    .reel-player {
        flex-grow: 1;

        &.-fullscreen {
            display: flex;
            flex-direction: column;
            position: fixed;
            width: 100vw;
            height: 100vh;
            top: 0;
            left: 0;
            z-index: 100;
        }
    }

    .reel-footer {
        display: none;
        height: 114px;
        background-color: #212C35;
        background-image: url(https://sportsrecruits.com/images/sr_otg/sr_logo_red_white.svg);
        background-position: center;
        background-repeat: no-repeat;
    }

    .-fullscreen .reel-footer {
        @media (orientation: portrait) {
            display: block;
        }
    }
</style>